/**
 * spin-game.js
 * Spinning Wheel Animation + API Calls für das Shopify Spin-to-Win Gewinnspiel.
 * Vanilla JS, keine externen Abhängigkeiten.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Konfiguration
  // ---------------------------------------------------------------------------
  const CONFIG          = window.__spinGame || {};
  const BACKEND_URL     = (CONFIG.backendUrl || '').replace(/\/$/, '');
  const MIN_ORDER_VALUE = CONFIG.minOrderValue || 25;

  if (BACKEND_URL) localStorage.setItem('spin_game_backend_url', BACKEND_URL);
  localStorage.setItem('spin_game_min_order_value', String(MIN_ORDER_VALUE));

  const RARITY_LABELS = {
    common: 'Gewöhnlich', uncommon: 'Ungewöhnlich', rare: 'Selten',
    epic: 'Episch', legendary: 'Legendär',
  };

  // ---------------------------------------------------------------------------
  // Cookie-ID
  // ---------------------------------------------------------------------------
  function getOrCreateCookieId() {
    const KEY = 'spin_game_cid';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
          });
      localStorage.setItem(KEY, id);
    }
    return id;
  }
  const COOKIE_ID = getOrCreateCookieId();

  // ---------------------------------------------------------------------------
  // DOM Elemente
  // ---------------------------------------------------------------------------
  const root = document.getElementById('spin-game');
  if (!root) return;

  const stateLoading   = document.getElementById('state-loading');
  const stateEnded     = document.getElementById('state-ended');
  const stateEndedMsg  = document.getElementById('state-ended-message');
  const statePlayed    = document.getElementById('state-played');
  const slotMachine    = document.getElementById('slot-machine');
  const btnSpin        = document.getElementById('btn-spin');
  const resultPanel    = document.getElementById('result-panel');
  const resultImg      = document.getElementById('result-img');
  const resultTitle    = document.getElementById('result-title');
  const resultRarity   = document.getElementById('result-rarity');
  const btnClaim       = document.getElementById('btn-claim');
  const alreadyWonInfo = document.getElementById('already-won-info');
  const btnClaimReturn = document.getElementById('btn-claim-return');

  let wonProduct    = null;
  let wonVariantId  = null;
  let loadedProducts = [];

  function show(el) { if (el) { el.hidden = false; el.style.display = ''; } }
  function hide(el) { if (el) { el.hidden = true;  el.style.display = 'none'; } }
  function trunc(s, n) { return s && s.length > n ? s.slice(0, n - 1) + '…' : (s || ''); }

  function wrapWheelText(str, maxPerLine) {
    if (str.length <= maxPerLine) return [str];
    const words = str.split(' ');
    if (words.length < 2) return [str];
    let best = 1, bestDiff = Infinity;
    for (let i = 1; i < words.length; i++) {
      const diff = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length);
      if (diff < bestDiff) { bestDiff = diff; best = i; }
    }
    return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
  }

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------
  async function apiFetch(path, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'X-Spin-Cookie-Id': COOKIE_ID,
      ...(options.headers || {}),
    };
    const res  = await fetch(`${BACKEND_URL}${path}`, { ...options, headers });
    const json = await res.json();
    if (!res.ok) throw Object.assign(new Error(json.error || 'API Fehler'), { status: res.status, data: json });
    return json;
  }
  const checkEligibility = ()     => apiFetch('/api/eligibility');
  const postSpin         = ()     => apiFetch('/api/spin', { method: 'POST' });
  const getProducts      = ()     => apiFetch('/api/products');
  const postClaim        = id     => apiFetch('/api/claim', { method: 'POST', body: JSON.stringify({ product_id: id }) });

  // ---------------------------------------------------------------------------
  // Wheel — Konstanten
  // ---------------------------------------------------------------------------
  const RARITY_COLORS = {
    legendary: '#21c7ce',
    epic:      '#f8dae7',
    rare:      '#f2b7d1',
    common:    '#8499ea',
    niete:     '#e2e2e2',
  };
  const RARITY_TEXT_COLORS = {
    legendary: 'rgba(255,255,255,0.96)',
    epic:      '#555',
    rare:      '#555',
    common:    'rgba(255,255,255,0.96)',
    niete:     '#444',
  };
  const NIETE_SLICE = { id: 'niete', title: 'Niete', image: null, rarityTier: 'niete', isNiete: true };
  const NUM_SLICES  = 8;

  let wheelCanvas = null;
  let wheelCtx    = null;
  let wheelRot    = 0;
  let wheelSlices = [];
  const wImgs     = {};

  // ---------------------------------------------------------------------------
  // Wheel — Aufbau
  // ---------------------------------------------------------------------------
  function buildWheelSlices(products) {
    const items = [];
    for (let i = 0; i < 7; i++) items.push(products[i % products.length]);
    items.push(NIETE_SLICE);
    return items;
  }

  function preloadWheelImages(callback) {
    const toLoad = wheelSlices.filter(s => s.image && !s.isNiete);
    if (!toLoad.length) { callback(); return; }
    let done = 0;
    wheelSlices.forEach((s, i) => {
      if (!s.image || s.isNiete) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { wImgs[i] = img; if (++done >= toLoad.length) callback(); };
      img.onerror = () => {           if (++done >= toLoad.length) callback(); };
      img.src = s.image;
    });
  }

  function initCanvas() {
    wheelCanvas = document.getElementById('wheel-canvas');
    if (!wheelCanvas) return false;
    wheelCtx = wheelCanvas.getContext('2d');
    const dpr  = window.devicePixelRatio || 1;
    const machine = wheelCanvas.closest('.spin-game__machine');
    const availWidth = machine ? machine.clientWidth : window.innerWidth - 48;
    const size = Math.min(520, availWidth);
    wheelCanvas.style.width  = size + 'px';
    wheelCanvas.style.height = size + 'px';
    wheelCanvas.width  = size * dpr;
    wheelCanvas.height = size * dpr;
    wheelCtx.scale(dpr, dpr);
    return true;
  }

  // ---------------------------------------------------------------------------
  // Wheel — Zeichnen
  // ---------------------------------------------------------------------------
  function drawWheel(rot) {
    if (!wheelCtx || !wheelCanvas) return;
    const ctx = wheelCtx;
    const dpr = window.devicePixelRatio || 1;
    const W   = wheelCanvas.width / dpr;
    const cx  = W / 2, cy = W / 2;
    const R   = W / 2 - 6;
    const arc = (2 * Math.PI) / NUM_SLICES;

    ctx.clearRect(0, 0, W, W);

    // Schatten
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.15)';
    ctx.shadowBlur  = 28;
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, 2 * Math.PI);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.restore();

    // Segmente
    wheelSlices.forEach((slice, i) => {
      const start = rot + i * arc - Math.PI / 2;
      const end   = start + arc;
      const mid   = start + arc / 2;

      // Füllung
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, start, end);
      ctx.closePath();
      ctx.fillStyle = RARITY_COLORS[slice.rarityTier] || RARITY_COLORS.common;
      ctx.fill();

      // Trennlinien
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + R * Math.cos(start), cy + R * Math.sin(start));
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth   = 3;
      ctx.stroke();

      // Produktbild (Kreis-Clip bei 61% Radius)
      const ir = R * 0.61;
      const ix = cx + ir * Math.cos(mid);
      const iy = cy + ir * Math.sin(mid);
      const is = R * 0.155;

      if (wImgs[i]) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(ix, iy, is, 0, 2 * Math.PI);
        ctx.clip();
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.drawImage(wImgs[i], ix - is, iy - is, is * 2, is * 2);
        ctx.restore();
        // Weißer Ring
        ctx.beginPath();
        ctx.arc(ix, iy, is, 0, 2 * Math.PI);
        ctx.strokeStyle = 'rgba(255,255,255,0.92)';
        ctx.lineWidth   = 2.5;
        ctx.stroke();
      }

      // Textlabel (radial ausgerichtet)
      ctx.save();
      const fontSize = Math.max(10, Math.round(R * 0.058));
      const tr = R * (wImgs[i] ? 0.87 : 0.70);
      ctx.translate(cx + tr * Math.cos(mid), cy + tr * Math.sin(mid));
      ctx.rotate(mid + Math.PI / 2);
      ctx.fillStyle    = RARITY_TEXT_COLORS[slice.rarityTier] || 'rgba(255,255,255,0.96)';
      ctx.font         = `bold ${fontSize}px Montserrat,sans-serif`;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      if (slice.isNiete) {
        const lh = fontSize * 1.3;
        ctx.font = `${Math.round(fontSize * 1.5)}px sans-serif`;
        ctx.fillText('☹', 0, -lh / 2);
        ctx.font = `bold ${fontSize}px Montserrat,sans-serif`;
        ctx.fillText('Niete', 0, lh / 2);
      } else {
        const raw   = (slice.title || '').split(' – ')[0];
        const lines = wrapWheelText(raw, 11);
        const lh    = fontSize * 1.3;
        if (lines.length === 1) {
          ctx.fillText(trunc(lines[0], 14), 0, 0);
        } else {
          ctx.fillText(trunc(lines[0], 13), 0, -lh / 2);
          ctx.fillText(trunc(lines[1], 13), 0,  lh / 2);
        }
      }
      ctx.restore();
    });

    // Äußerer Rand
    ctx.beginPath();
    ctx.arc(cx, cy, R, 0, 2 * Math.PI);
    ctx.strokeStyle = 'rgba(0,0,0,0.1)';
    ctx.lineWidth   = 4;
    ctx.stroke();

    // Nabe (Mitte)
    const hr = R * 0.12;
    const g  = ctx.createRadialGradient(cx, cy, 0, cx, cy, hr);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(1, '#e0e0e0');
    ctx.beginPath();
    ctx.arc(cx, cy, hr, 0, 2 * Math.PI);
    ctx.fillStyle = g;
    ctx.fill();
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth   = 2;
    ctx.stroke();
  }

  // ---------------------------------------------------------------------------
  // Wheel — Animation
  // ---------------------------------------------------------------------------
  function findWinnerSliceIndex(winner) {
    const hits = wheelSlices.reduce((a, s, i) => {
      if (!s.isNiete && String(s.id) === String(winner.id)) a.push(i);
      return a;
    }, []);
    return hits.length ? hits[Math.floor(Math.random() * hits.length)] : 0;
  }

  async function animateWheel(winner) {
    const arc  = (2 * Math.PI) / NUM_SLICES;
    const tidx = findWinnerSliceIndex(winner);

    // Zielrotation: Segment tidx unter dem Zeiger (oben = −π/2)
    // Segment-Mitte i ist bei: rot + (i + 0.5) * arc − π/2
    // Gewünscht: rot_final + (tidx + 0.5) * arc − π/2 ≡ −π/2 (mod 2π)
    // → rot_final = −tidx * arc − arc/2 + 2π * k
    const spins  = 5 + Math.floor(Math.random() * 3);
    const jitter = (Math.random() - 0.5) * arc * 0.55;
    let   target = -(tidx * arc + arc / 2) + jitter + spins * 2 * Math.PI;
    while (target <= wheelRot) target += 2 * Math.PI;

    const startRot = wheelRot;
    const delta    = target - startRot;
    const dur      = 5500;
    const t0       = performance.now();
    let   lastSl   = -1;

    return new Promise(resolve => {
      function frame(now) {
        const p  = Math.min(1, (now - t0) / dur);
        const e  = 1 - Math.pow(1 - p, 4); // ease-out quart

        wheelRot = startRot + delta * e;
        drawWheel(wheelRot);

        // Klick-Sound an Segmentgrenzen
        const sl = Math.floor(((wheelRot % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI)) / arc);
        if (sl !== lastSl) { playClick(0.22 + e * 0.18, 140 + e * 90); lastSl = sl; }

        if (p < 1) {
          requestAnimationFrame(frame);
        } else {
          wheelRot = target;
          drawWheel(wheelRot);
          resolve();
        }
      }
      requestAnimationFrame(frame);
    });
  }

  // ---------------------------------------------------------------------------
  // Audio
  // ---------------------------------------------------------------------------
  let _audioCtx = null;
  function getAudioCtx() {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    return _audioCtx;
  }
  function playClick(vol, pitch) {
    try {
      const ctx  = getAudioCtx();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.setValueAtTime(pitch, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(pitch * 0.4, ctx.currentTime + 0.045);
      gain.gain.setValueAtTime(vol, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.055);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.06);
    } catch (_) {}
  }
  function playWinSound() {
    try {
      const ctx = getAudioCtx();
      [330, 440, 660].forEach((freq, i) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine'; osc.frequency.value = freq;
        const t = ctx.currentTime + i * 0.13;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.45, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
        osc.start(t); osc.stop(t + 0.6);
      });
    } catch (_) {}
  }

  // ---------------------------------------------------------------------------
  // Konfetti
  // ---------------------------------------------------------------------------
  (function () {
    if (document.getElementById('spin-confetti-keyframes')) return;
    const s = document.createElement('style');
    s.id = 'spin-confetti-keyframes';
    s.textContent = `
      @keyframes confetti-fall { 0%{transform:translateY(-10px) rotate(0deg) scale(1);opacity:1} 85%{opacity:1} 100%{transform:translateY(105vh) rotate(720deg) scale(0.6);opacity:0} }
      @keyframes confetti-sway { 0%{margin-left:0} 25%{margin-left:20px} 75%{margin-left:-20px} 100%{margin-left:0} }
    `;
    document.head.appendChild(s);
  })();

  function launchConfetti(rarity) {
    const pal = {
      common:    ['#9E9E9E','#BDBDBD','#fff'],
      uncommon:  ['#4CAF50','#81C784','#fff'],
      rare:      ['#2196F3','#64B5F6','#fff'],
      epic:      ['#9C27B0','#CE93D8','#E040FB'],
      legendary: ['#FFB300','#FF6B35','#FFF176','#FFD54F'],
    };
    const cnt = { common:50, uncommon:60, rare:80, epic:100, legendary:140 };
    const palette = pal[rarity] || pal.common;
    const count   = cnt[rarity] || 60;
    const c = document.createElement('div');
    c.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;overflow:hidden;';
    c.style.setProperty('display', 'block', 'important');
    document.body.appendChild(c);
    for (let i = 0; i < count; i++) {
      const p     = document.createElement('div');
      const color = palette[i % palette.length];
      const size  = Math.random() * 9 + 5, left = Math.random() * 100;
      const delay = Math.random() * 1.2,   dur  = Math.random() * 1.5 + 2.5;
      const sw    = Math.random() * 1.5 + 1.5, rect = Math.random() > 0.4;
      p.style.cssText = `position:absolute;top:-20px;left:${left}%;width:${size}px;height:${rect ? size * 0.45 : size}px;background:${color};border-radius:${rect ? '2px' : '50%'};animation:confetti-fall ${dur}s ease-in ${delay}s forwards,confetti-sway ${sw}s ease-in-out ${delay}s infinite;display:block!important;`;
      c.appendChild(p);
    }
    setTimeout(() => c.remove(), 5500);
  }

  // ---------------------------------------------------------------------------
  // Ergebnis anzeigen
  // ---------------------------------------------------------------------------
  function showResult(product, silent = false) {
    wonProduct = product;

    resultImg.src = product.image || '';
    resultImg.alt = product.title;
    if (!product.image) hide(resultImg); else show(resultImg);

    const sep = product.title.indexOf(' – ');
    resultTitle.innerHTML = '';
    if (sep !== -1) {
      const sp = document.createElement('span'); sp.className = 'result-title__product'; sp.textContent = product.title.slice(0, sep);
      const sv = document.createElement('span'); sv.className = 'result-title__variant';  sv.textContent = product.title.slice(sep + 3);
      resultTitle.appendChild(sp); resultTitle.appendChild(sv);
    } else {
      resultTitle.textContent = product.title;
    }
    resultRarity.textContent    = RARITY_LABELS[product.rarityTier] || product.rarityTier;
    resultRarity.dataset.rarity = product.rarityTier;
    resultPanel.dataset.rarity  = product.rarityTier;

    localStorage.setItem('spin_game_won_variant_id', String(product.id));
    localStorage.setItem('spin_game_won_title',      product.title);
    localStorage.setItem('spin_game_won_image',      product.image || '');

    if (!silent) { playWinSound(); launchConfetti(product.rarityTier); }
    show(resultPanel);
    resultPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (btnClaim) { btnClaim.disabled = false; btnClaim.style.opacity = ''; btnClaim.style.cursor = ''; }
  }

  // ---------------------------------------------------------------------------
  // Claim
  // ---------------------------------------------------------------------------
  async function addToCart(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: { '_spin_win': '1' } }),
    });
    if (!res.ok) {
      let m = 'Warenkorb-Fehler';
      try { const e = await res.json(); if (e.description) m = e.description; } catch (_) {}
      throw new Error(m);
    }
    localStorage.setItem('spin_game_won_variant_id', String(variantId));
    fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { '_spin_won_variant': 'gid://shopify/ProductVariant/' + variantId } }),
    }).catch(() => {});
  }

  async function goToCart() {
    const drawer  = document.getElementById('cart-drawer');
    const toggles = Array.from(document.querySelectorAll('[aria-controls="cart-drawer"]'));
    const openBtn = toggles.find(t => drawer && !drawer.contains(t)) || toggles[0];

    if (!openBtn) {
      window.location.href = '/cart';
      return;
    }

    // Drawer-Inhalt über Shopify Sections API aktualisieren, damit der neue Artikel sichtbar ist
    if (drawer) {
      try {
        const res  = await fetch('/?sections=cart-drawer');
        const data = await res.json();
        const html = data['cart-drawer'];
        if (html) {
          const doc   = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.getElementById('cart-drawer');
          if (fresh) drawer.innerHTML = fresh.innerHTML;
        }
      } catch (_) {}
    }

    // Drawer öffnen
    const isExpanded = openBtn.getAttribute('aria-expanded') === 'true';
    if (!isExpanded) {
      openBtn.click();
    } else {
      // Bereits offen → kurz schließen und wieder öffnen
      openBtn.click();
      setTimeout(() => openBtn.click(), 160);
    }
  }

  function showClaimHint(msg) {
    let h = document.getElementById('claim-min-hint');
    if (!h) {
      h = document.createElement('p'); h.id = 'claim-min-hint';
      h.style.cssText = 'color:#e53935;font-size:0.85rem;margin:8px 0 0;';
      const anchor = (btnClaimReturn && !btnClaimReturn.hidden) ? btnClaimReturn : btnClaim;
      if (anchor) anchor.insertAdjacentElement('afterend', h);
    }
    h.textContent = msg;
  }

  async function handleClaim() {
    if (!wonProduct) return;
    btnClaim.disabled = true; btnClaim.textContent = 'Wird verarbeitet…';
    try {
      await postClaim(wonProduct.id);
      await addToCart(wonProduct.id);
      btnClaim.textContent = '✓ Im Warenkorb!'; btnClaim.style.cursor = 'default';
      await goToCart();
    } catch (err) {
      console.error('[SpinGame] Claim-Fehler:', err); btnClaim.disabled = false;
      btnClaim.textContent = 'Fehler – erneut versuchen';
      showClaimHint(err.message || 'Fehler beim Hinzufügen.');
    }
  }

  // ---------------------------------------------------------------------------
  // Initialisierung
  // ---------------------------------------------------------------------------
  async function init() {
    show(stateLoading);
    try {
      const eligibility = await checkEligibility();
      hide(stateLoading);

      if (!eligibility.eligible) {
        const reason = eligibility.reason || '';
        if (reason.includes('bereits') || eligibility.wonProductId) {
          hide(statePlayed); hide(slotMachine);
          if (eligibility.wonProductId) {
            wonVariantId = eligibility.wonProductId;
            getProducts().then(({ products }) => {
              const match = (products || []).find(p => String(p.id) === String(eligibility.wonProductId));
              if (match) showResult(match, true);
              else { show(statePlayed); show(alreadyWonInfo); show(btnClaimReturn); }
            }).catch(() => show(statePlayed));
          }
        } else {
          if (stateEndedMsg) stateEndedMsg.textContent = reason;
          show(stateEnded);
        }
        return;
      }

      // Spielberechtigt → Rad aufbauen
      show(slotMachine);
      getProducts().then(({ products }) => {
        if (!products || !products.length) return;
        loadedProducts = products;
        if (!initCanvas()) return;
        wheelSlices = buildWheelSlices(products);
        preloadWheelImages(() => drawWheel(wheelRot));
      }).catch(() => {});

    } catch (err) {
      hide(stateLoading);
      console.error('Init-Fehler:', err);
      if (stateEndedMsg) stateEndedMsg.textContent = 'Verbindungsfehler. Bitte Seite neu laden.';
      show(stateEnded);
    }
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------
  if (btnSpin) {
    btnSpin.addEventListener('click', async () => {
      getAudioCtx();
      btnSpin.disabled = true; btnSpin.textContent = 'Dreht…';
      try {
        const data = await postSpin();
        await animateWheel(data.winning_product);
        await new Promise(r => setTimeout(r, 600));
        showResult(data.winning_product);
        btnSpin.textContent = 'Gedreht!';
      } catch (err) {
        console.error(err); btnSpin.disabled = false;
        if (err.status === 409) { btnSpin.textContent = 'Bereits gespielt'; hide(slotMachine); show(statePlayed); }
        else btnSpin.textContent = 'Fehler – erneut versuchen';
      }
    });
  }

  if (btnClaim) btnClaim.addEventListener('click', handleClaim);

  if (btnClaimReturn) {
    btnClaimReturn.addEventListener('click', async () => {
      const vid = wonProduct?.id || wonVariantId;
      if (!vid) return;
      btnClaimReturn.disabled = true; btnClaimReturn.textContent = 'Wird verarbeitet…';
      try {
        await postClaim(vid);
        await addToCart(vid);
        btnClaimReturn.textContent = '✓ Im Warenkorb!'; btnClaimReturn.style.cursor = 'default';
        await goToCart();
      } catch (err) {
        console.error(err); btnClaimReturn.disabled = false;
        btnClaimReturn.textContent = 'Kostenlos in den Warenkorb';
        showClaimHint(err.message || 'Fehler.');
      }
    });
  }

  init();
})();
